'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import {
  Search,
  FilePlus2,
  Eye,
  Pencil,
  Trash2,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Paperclip,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  X,
  AlertTriangle,
  Banknote,
} from 'lucide-react';

import Modal from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { fundRequests, formatTZS, type FundRequest } from '@/lib/mockData';

type SortKey = keyof FundRequest | null;
type SortDir = 'asc' | 'desc';
const PAGE_SIZE_OPTIONS = [5, 10, 20];

export default function ApprovedRequestsContent() {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<FundRequest | null>(null);
  const [releaseTarget, setReleaseTarget] = useState<FundRequest | null>(null);
  const [bankRefInput, setBankRefInput] = useState('');
  const [rows, setRows] = useState<FundRequest[]>(
    fundRequests.filter((r) => r.status === 'Approved')
  );

  const filtered = useMemo(() => {
    let data = rows;
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (r) =>
          r.requestId.toLowerCase().includes(q) ||
          r.requestorFirstName.toLowerCase().includes(q) ||
          r.requestorLastName.toLowerCase().includes(q) ||
          r.clientName.toLowerCase().includes(q) ||
          r.customerProject.toLowerCase().includes(q)
      );
    }
    if (sortKey) {
      data = [...data].sort((a, b) => {
        const av = a[sortKey] ?? '';
        const bv = b[sortKey] ?? '';
        const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return data;
  }, [rows, search, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronsUpDown size={12} className="text-muted-foreground/50" />;
    return sortDir === 'asc'
      ? <ChevronUp size={12} className="text-primary" />
      : <ChevronDown size={12} className="text-primary" />;
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === paginated.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(paginated.map((r) => r.id)));
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setRows((prev) => prev.filter((r) => r.id !== deleteTarget.id));
    toast('success', 'Request deleted', `${deleteTarget.requestId} removed from approved queue.`);
    setDeleteTarget(null);
  };

  const confirmRelease = () => {
    if (!releaseTarget) return;
    if (!bankRefInput.trim()) {
      toast('error', 'Bank reference required', 'Enter a bank reference number to release funds.');
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== releaseTarget.id));
    // Backend integration point: PATCH /api/fund-requests/:id { status: 'Released', bankReference, releaseDate }
    toast('success', 'Funds released', `${releaseTarget.requestId} marked as Released — bank ref ${bankRefInput}`);
    setReleaseTarget(null);
    setBankRefInput('');
  };

  const totalApprovedAmount = rows.reduce((s, r) => s + r.amountRequested, 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-700 text-foreground">Approved Requests</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            EXCO-approved fund requests awaiting finance release —{' '}
            <span className="font-600 text-success">{rows.length} approved</span>
          </p>
        </div>
        <Link
          href="/project-fund-request-form"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-600 hover:bg-primary/90 transition-all duration-150 active:scale-95 shadow-card"
        >
          <FilePlus2 size={15} />
          New Fund Request
        </Link>
      </div>

      {/* Summary bar */}
      <div className="bg-success-bg border border-success/20 rounded-xl px-5 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CheckCircle2 size={16} className="text-success" />
          <span className="text-sm font-600 text-foreground">
            {rows.length} requests approved — total{' '}
            <span className="text-success tabular-nums">{formatTZS(totalApprovedAmount)}</span> pending release
          </span>
        </div>
        <span className="text-xs text-muted-foreground">Finance team: review and release below</span>
      </div>

      {/* Filters */}
      <div className="relative max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search requests…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="w-full pl-8 pr-3 py-2 text-sm border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={paginated.length > 0 && selectedIds.size === paginated.length}
                    onChange={toggleAll}
                    className="rounded border-input"
                    aria-label="Select all approved rows"
                  />
                </th>
                {([
                  ['requestId', 'Request ID'],
                  ['requestorFirstName', 'Requestor'],
                  ['clientName', 'Client Name'],
                  ['customerProject', 'Project'],
                  ['expenseType', 'Expense Type'],
                  ['amountRequested', 'Amount (TZS)'],
                  ['bankReference', 'Bank Reference'],
                  ['attachmentCount', 'Files'],
                  ['createdAt', 'Approved On'],
                ] as [SortKey, string][]).map(([key, label]) => (
                  <th
                    key={`th-appr-${key}`}
                    className="text-left px-4 py-3 text-[11px] font-600 uppercase tracking-widest text-muted-foreground whitespace-nowrap cursor-pointer select-none hover:text-foreground transition-colors"
                    onClick={() => handleSort(key)}
                  >
                    <span className="inline-flex items-center gap-1">
                      {label}
                      <SortIcon col={key} />
                    </span>
                  </th>
                ))}
                <th className="px-4 py-3 text-[11px] font-600 uppercase tracking-widest text-muted-foreground text-right whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-16 text-center">
                    <CheckCircle2 size={32} className="mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-sm font-600 text-foreground">No approved requests</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      No fund requests have been approved yet, or all approved funds have been released.
                    </p>
                  </td>
                </tr>
              ) : (
                paginated.map((req, idx) => (
                  <tr
                    key={req.id}
                    className={`border-b border-border last:border-0 hover:bg-muted/40 transition-colors duration-100 ${
                      idx % 2 === 1 ? 'bg-muted/20' : ''
                    } ${selectedIds.has(req.id) ? 'bg-primary/5' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(req.id)}
                        onChange={() => toggleSelect(req.id)}
                        className="rounded border-input"
                        aria-label={`Select ${req.requestId}`}
                      />
                    </td>
                    <td className="px-4 py-3 font-600 text-primary tabular-nums text-xs whitespace-nowrap">
                      {req.requestId}
                    </td>
                    <td className="px-4 py-3 text-foreground whitespace-nowrap">
                      {req.requestorFirstName} {req.requestorLastName}
                    </td>
                    <td className="px-4 py-3 text-foreground whitespace-nowrap max-w-[140px] truncate">
                      {req.clientName}
                    </td>
                    <td className="px-4 py-3 text-foreground max-w-[160px] truncate">
                      {req.customerProject}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                      {req.expenseType}
                    </td>
                    <td className="px-4 py-3 text-right font-600 tabular-nums text-foreground whitespace-nowrap">
                      {formatTZS(req.amountRequested)}
                    </td>
                    <td className="px-4 py-3">
                      {req.bankReference ? (
                        <span className="text-xs font-500 text-foreground tabular-nums">{req.bankReference}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-warning">
                          <AlertTriangle size={11} />
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {req.attachmentCount > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Paperclip size={12} />
                          {req.attachmentCount}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/40">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap tabular-nums">
                      {req.createdAt}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          title="Release funds for this request"
                          onClick={() => { setReleaseTarget(req); setBankRefInput(req.bankReference || ''); }}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-success hover:bg-success/10 transition-all duration-150"
                        >
                          <Banknote size={14} />
                        </button>
                        <button
                          title="View request details"
                          className="p-1.5 rounded-md text-muted-foreground hover:text-info hover:bg-info/10 transition-all duration-150"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          title="Edit this request"
                          className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-150"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          title="Delete this request — this cannot be undone"
                          onClick={() => setDeleteTarget(req)}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-danger hover:bg-danger/10 transition-all duration-150"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-border bg-muted/20">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Show</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="border border-input rounded-md px-2 py-1 text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {PAGE_SIZE_OPTIONS.map((s) => (
                <option key={`ps-appr-${s}`} value={s}>{s}</option>
              ))}
            </select>
            <span>of {filtered.length} requests</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={`page-appr-${i + 1}`}
                onClick={() => setPage(i + 1)}
                className={`w-7 h-7 rounded-md text-xs font-600 transition-all duration-150 ${
                  page === i + 1
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-foreground text-background rounded-xl px-5 py-3 shadow-modal slide-up">
          <span className="text-sm font-600">{selectedIds.size} selected</span>
          <div className="w-px h-4 bg-background/20" />
          <button
            onClick={() => {
              const count = selectedIds.size;
              setRows((prev) => prev.filter((r) => !selectedIds.has(r.id)));
              setSelectedIds(new Set());
              toast('success', `${count} request${count > 1 ? 's' : ''} deleted`);
            }}
            className="text-sm font-600 text-danger hover:text-danger/80 transition-colors inline-flex items-center gap-1.5"
          >
            <Trash2 size={14} />
            Delete selected
          </button>
          <button onClick={() => setSelectedIds(new Set())} className="text-sm text-background/60 hover:text-background transition-colors">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Release modal */}
      <Modal
        open={releaseTarget !== null}
        onClose={() => { setReleaseTarget(null); setBankRefInput(''); }}
        title="Release Funds"
        size="sm"
      >
        <p className="text-sm text-muted-foreground mb-4">
          Releasing funds for{' '}
          <span className="font-700 text-foreground">{releaseTarget?.requestId}</span> —{' '}
          <span className="font-700 text-success tabular-nums">
            {releaseTarget ? formatTZS(releaseTarget.amountRequested) : ''}
          </span>
        </p>
        <div className="mb-5">
          <label className="block text-xs font-600 text-foreground mb-1" htmlFor="bankRefModal">
            Bank Reference <span className="text-danger">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">Enter the bank transaction reference number</p>
          <input
            id="bankRefModal"
            type="text"
            placeholder="e.g. CRDB-2026-44821"
            value={bankRefInput}
            onChange={(e) => setBankRefInput(e.target.value)}
            className="w-full rounded-md border border-input px-3 py-2 text-sm text-foreground bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => { setReleaseTarget(null); setBankRefInput(''); }}
            className="px-3 py-2 rounded-md border border-border text-sm font-600 text-foreground hover:bg-muted transition-all duration-150"
          >
            Cancel
          </button>
          <button
            onClick={confirmRelease}
            className="px-3 py-2 rounded-md bg-success text-white text-sm font-600 hover:bg-success/90 transition-all duration-150 active:scale-95 inline-flex items-center gap-1.5"
          >
            <Banknote size={14} />
            Confirm Release
          </button>
        </div>
      </Modal>

      {/* Delete modal */}
      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Delete Approved Request"
        size="sm"
      >
        <p className="text-sm text-muted-foreground mb-1">
          Delete <span className="font-700 text-foreground">{deleteTarget?.requestId}</span>?
        </p>
        <p className="text-xs text-muted-foreground mb-5">This will remove the request from the approved queue permanently.</p>
        <div className="flex gap-2 justify-end">
          <button onClick={() => setDeleteTarget(null)} className="px-3 py-2 rounded-md border border-border text-sm font-600 text-foreground hover:bg-muted transition-all duration-150">
            Cancel
          </button>
          <button onClick={confirmDelete} className="px-3 py-2 rounded-md bg-danger text-white text-sm font-600 hover:bg-danger/90 transition-all duration-150 active:scale-95">
            Delete Request
          </button>
        </div>
      </Modal>
    </div>
  );
}